// Screen 6 - SDK install step (slice-2-onboarding.md §9; ADR-0016 §6: crew-installed,
// never a founder-pasted snippet). A progress list the founder can watch or leave, ending
// in a PR to merge, plus the key-shown-once card for the founder's own backup copy.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper } from "../../components/Stepper.js";
import { DeniedCard } from "../../components/DeniedCard.js";
import { KeyShownOnce } from "../../components/KeyShownOnce.js";
import { materializeSdkInstall } from "../../api/sdk.js";
import type { SdkInstallResponse } from "../../api/sdk.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";

// Fixture-first progress derivation (ADR-0016 §6): the recipe is prepared and the key
// registered synchronously; no real PR exists until connections.real_git_host flips, so
// pullRequestUrl stays null and the merge step stays pending. The real adapter will
// carry live statuses through the same response shape.
const deriveProgress = (
  response: SdkInstallResponse,
): {
  readonly openingPr: "done" | "pending";
  readonly registeringKey: "done" | "pending";
  readonly waitingForMerge: "done" | "pending";
  readonly pullRequestUrl: string | null;
} => ({
  openingPr: "done",
  registeringKey: response.appKeyId.length > 0 ? "done" : "pending",
  waitingForMerge: "pending",
  pullRequestUrl: null,
});

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly recipe: ReturnType<typeof deriveProgress>;
      readonly rawKey: string | null;
    };

export const SdkInstall = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { state: session } = useSession();
  const { draft, reset } = useWizard();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    const { appId } = draft;
    if (appId === null) return;
    const cancelledRef = { current: false };
    // See src/state/session.tsx's useEffect for why this is a function call, not a
    // direct property read (defeats a stale no-unnecessary-condition narrowing across
    // the two `await`s below).
    const isCancelled = (): boolean => cancelledRef.current;
    void (async () => {
      // One call: sdk-install mints the app key itself when absent (the raw key rides
      // back once in `writeKey`) - a separate app-keys POST here would mint a second key.
      const result = await materializeSdkInstall(appId);
      if (isCancelled()) return;
      if (isErr(result)) {
        setState({ kind: "error" });
        return;
      }
      setState({
        kind: "ready",
        recipe: deriveProgress(result.value),
        rawKey: result.value.writeKey ?? null,
      });
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [draft.appId]);

  if (!isOwnerOrAdmin(session)) {
    return (
      <FocusedFlowShell title="Install the SDK">
        <DeniedCard message="Only an owner or admin can install the SDK and create an app key." />
      </FocusedFlowShell>
    );
  }

  return (
    <FocusedFlowShell
      title="Install the SDK"
      onBack={() => {
        void navigate("/apps/new/route");
      }}
    >
      <OuterStepper active="first_shift" />

      {state.kind === "loading" ? (
        <div className="progress-list mt16">
          <div className="progress-item">
            <span className="dot info" /> Opening a pull request...
          </div>
        </div>
      ) : null}

      {state.kind === "error" ? (
        <p className="field-error" role="alert">
          We could not open the install PR. Reconnect and try again.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <div className="mt16">
          <div className="progress-list">
            <div className="progress-item">
              <span className={`dot ${state.recipe.openingPr === "done" ? "good" : "info"}`} />
              {state.recipe.openingPr === "done" ? "Pull request opened" : "Opening a pull request"}
            </div>
            <div className="progress-item">
              <span className={`dot ${state.recipe.registeringKey === "done" ? "good" : "info"}`} />
              {state.recipe.registeringKey === "done"
                ? "App key registered"
                : "Registering your app key"}
            </div>
            <div className="progress-item">
              <span
                className={`dot ${state.recipe.waitingForMerge === "done" ? "good" : "info"}`}
              />
              {state.recipe.waitingForMerge === "done" ? "Merged" : "Waiting for merge"}
            </div>
          </div>

          {state.rawKey !== null ? (
            <div className="mt16">
              <KeyShownOnce rawKey={state.rawKey} />
            </div>
          ) : null}

          {state.recipe.pullRequestUrl !== null ? (
            <a
              className="chip mt16"
              href={state.recipe.pullRequestUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the pull request
            </a>
          ) : null}

          {state.recipe.waitingForMerge === "done" ? (
            <div className="card pad mt16">
              <p>SDK installed. Passive telemetry and the feedback widget are live.</p>
              <button
                type="button"
                className="btn btn-primary mt12"
                onClick={() => {
                  reset();
                  void navigate("/apps");
                }}
              >
                Go to your app
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-ghost mt16"
              onClick={() => {
                void navigate("/apps");
              }}
            >
              I&apos;ll do this later
            </button>
          )}
        </div>
      ) : null}
    </FocusedFlowShell>
  );
};
